import { useValidateEmailConfirmationCode } from "@tandemdiabetes/sugarmate-api-react-client"
import { useToasterContext } from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"
import type { FormEventHandler } from "react"

import { useOverlayLoader } from "src/contexts"
import { ValidateEmailConfirmationCodeErrorToast } from "src/toasts"
import {
  injectProps,
  getSugarmateError,
} from "src/utils"

interface Params {
  emailAddress: string;
  emailConfirmationCode: string;
  onSuccessCallback: (emailAddress: string, confirmationCode: string) => void;
  onInvalidCallback: (code: string) => void;
}

/*
 * this hook returns a function that validates the emailConfirmationCode
 * before calling the onSuccessCallback
 *
 * in the case the server deems the submitted code invalid or expired it will
 * execute the onInvalidCallback
 */
export function useOnSubmitEmailVerificationCode(params: Params) {
  const {
    emailAddress,
    emailConfirmationCode,
    onSuccessCallback,
    onInvalidCallback,
  } = params

  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const validateEmailConfirmationCode = useValidateEmailConfirmationCode()
  return useCallback<FormEventHandler<HTMLFormElement>>(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      if (!validateEmailConfirmationCode) {
        showToast(
          "validate-email-confirmation-code-error-toast",
          ValidateEmailConfirmationCodeErrorToast,
        )

        return
      }

      void (async () => {
        setIsOverlayLoaderVisible(true)

        try {
          await validateEmailConfirmationCode({
            email: emailAddress,
            code: emailConfirmationCode,
          })
          onSuccessCallback(
            emailAddress,
            emailConfirmationCode,
          )
        } catch (e) {
          const sugarmateError = getSugarmateError(e)
          const code = sugarmateError?.code

          // the status code is invalid or expired
          if (code === 1044) {
            onInvalidCallback(emailConfirmationCode)
          } else {
            const ErrorToast = injectProps(
              ValidateEmailConfirmationCodeErrorToast,
              { status: sugarmateError?.status },
            )

            showToast(
              "validate-email-confirmation-code-error-toast",
              ErrorToast,
            )
          }
        } finally {
          setIsOverlayLoaderVisible(false)
        }
      })()
    },
    [
      emailAddress,
      emailConfirmationCode,
      validateEmailConfirmationCode,
      showToast,
      setIsOverlayLoaderVisible,
      onInvalidCallback,
      onSuccessCallback,
    ],
  )
}
